import React from 'react';

import Button from '../../components/Button';
import Footer from '../../components/Footer';
import Navigation from '../../components/Navigation';
import { OrangeLink } from '../../components/Link';
import Page from '../../components/Page';
import Section from '../../components/Section';
import Theme from '../../components/Theme';
import { themeSecondary } from '../../styles/constants';
import { BetaHeading, GammaHeading } from '../../components/Typography';

import { Paragraph, SingleLine, List, NumberList, ListItem } from './style';

const FundingInformation = () => (
  <Theme theme={themeSecondary}>
    <Navigation>
      <OrangeLink border to="/">
        Back to search
      </OrangeLink>
    </Navigation>
    <Page>
      <Section>
        <BetaHeading>Funding information</BetaHeading>
        <Paragraph>
          In Australia, the government provides funding to some people who have
          a disability as well as some people who are elderly and need
          assistance with daily living.
        </Paragraph>
        <GammaHeading>Disability Support</GammaHeading>
        <Paragraph>
          The{' '}
          <a
            href="https://www.ndis.gov.au/about-us/what-ndis"
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            National Disability Insurance Scheme (NDIS)
          </a>{' '}
          is the new way of providing support for Australians with disability,
          their families and carers.
        </Paragraph>
        <Paragraph>
          The NDIS supports people under the age of 65 with a permanent and
          significant disability with the reasonable and necessary supports they
          need to live an ordinary life.
        </Paragraph>
        <SingleLine>The NDIS helps people with disability to:</SingleLine>
        <List>
          <ListItem>
            Access mainstream services and supports - services that are
            available for all Australians from people like doctors or teachers
            through the health and education systems. It also covers areas like
            public housing and the justice and aged care systems.
          </ListItem>
          <ListItem>
            Access community services and supports - activities and services
            that are available to everyone in a community, such as sports clubs,
            community groups, libraries or charities.
          </ListItem>
          <ListItem>
            Maintain informal support arrangements - to help people get
            assistance from their family and friends.
          </ListItem>
          <ListItem>
            Receive reasonable and necessary funded support - This means they
            are related to a person’s disability and are required for them to
            live an ordinary life and achieve their goals.
          </ListItem>
        </List>
        <Paragraph>
          Assistance from the NDIS is not means tested and has no impact on
          income support such as the Disability Support Pension and Carers
          Allowance.
        </Paragraph>
        <SingleLine>
          See the{' '}
          <a
            href="https://www.ndis.gov.au/"
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            NDIS website
          </a>{' '}
          for more details.
        </SingleLine>
        <GammaHeading>Aged Care</GammaHeading>
        <Paragraph>
          Aged care support is commonly referred to as home care and is
          available for people who are over 65 years old. There are two main
          home care funding streams available:
        </Paragraph>
        <NumberList>
          <ListItem>
            The Commonwealth Home Support Programme{' '}
            <a
              href="https://www.myagedcare.gov.au/help-home/commonwealth-home-support-programme/accessing-commonwealth-home-support-programme"
              rel="nofollow noopener noreferrer"
              target="_blank"
            >
              (CHSP)
            </a>. This is designed to help older Australians remain independent
            in their own homes and communities for longer. Funding covers
            services to help with daily tasks and can even help carers with
            planned respite.
          </ListItem>
          <ListItem>
            <a
              href="https://www.myagedcare.gov.au/help-home/home-care-packages"
              rel="nofollow noopener noreferrer"
              target="_blank"
            >
              Home Care Packages
            </a>. Home Care Packages offer choice and flexibility for
            recipients. Packages are tailored to meet a range of care needs and
            are offered at four levels depending on the amount of support
            required. There are four levels - Level 1: Basic care package
            through to Level 4: High level care package
          </ListItem>
        </NumberList>
        <Paragraph>
          With the introduction of{' '}
          <a
            href="https://www.myagedcare.gov.au/help-home/home-care-packages/consumer-directed-care-cdc"
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            Consumer Directed Care (CDC)
          </a>{' '}
          Home Care Packages are now controlled by the recipient rather than the
          service provider. This means that you can compare providers to find
          one that best meets your individual needs. Today, it is much easier to
          change providers. If you make a change, the funding for your package
          will follow you to your new provider.
        </Paragraph>
        <Paragraph>
          Assessments for both the CHSP and Home Care Package are facilitated by
          the Australian Government through{' '}
          <a
            href="http://www.myagedcare.gov.au"
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            MyAgedCare
          </a>. For the Commonwealth Home Support Programme, you may need a home
          support assessment. Home Care Packages are planned by an Aged Care
          Assessment Team (ACAT) or Aged Care Assessment Service (ACAS) in
          Victoria.
        </Paragraph>
        <Paragraph>Other available funding programs include:</Paragraph>
        <List>
          <ListItem>
            <a
              href="https://www.dva.gov.au/health-and-wellbeing/home-and-care/aged-and-community-care#vhc"
              rel="nofollow noopener noreferrer"
              target="_blank"
            >
              Department of Veteran&apos;s Affairs (DVA)
            </a>
          </ListItem>
          <ListItem>
            <a
              href="https://www.healthdirect.gov.au/government-programs-that-support-respite"
              rel="nofollow noopener noreferrer"
              target="_blank"
            >
              National Respite for Carers (NRCP) or Consumer Directed Respite
              Care (CDRC)
            </a>
          </ListItem>
        </List>
        <Paragraph>Some people may be covered for services through:</Paragraph>
        <List>
          <ListItem>Private health insurance</ListItem>
          <ListItem>Workplace injury insurance</ListItem>
          <ListItem>Motor accident insurance</ListItem>
        </List>
        <Paragraph>
          While funding is provided by the above funding programs, often people
          wish to spend their own money
        </Paragraph>
        <Paragraph>
          <a href="http://www.karista.com.au">Karista</a> can assist people who
          either receive government funding, or are paying for services from
          their own money. Either way, it is free to carers and care recipients
          who are looking for services. The process is quick and easy.
        </Paragraph>
        <Paragraph>
          Visit{' '}
          <a
            href="http://www.karista.com.au"
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            www.karista.com.au
          </a>
          &nbsp;or call 1300 274782 for assistance to compare providers and
          compare quotes to understand the cost of selected providers and make
          the right choice for you.
        </Paragraph>
        <Button
          onClick={() => {
            window.scroll(0, 0);
          }}
        >
          Back to top
        </Button>
      </Section>
    </Page>
    <Footer />
  </Theme>
);

export default FundingInformation;
